import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import { waybillAPI } from '../../api/waybillAPI';

const PHP = (n) =>
  Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = () => new Date().toISOString().split('T')[0];

const getStartOfMonth = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split('T')[0];
};

const formatDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleDateString('en-PH', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
  });
};

function PaymentStatusBadge({ status }) {
  if (!status) return <span className="text-xs text-gray-400">No Billing</span>;
  if (status === 'paid')
    return <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">Paid</span>;
  if (status === 'partially_paid')
    return <span className="px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-700">Partial</span>;
  return <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-600">Unpaid</span>;
}

const emptyData = { rows: [], vanGroups: {}, grandTotal: 0 };

function exportCSV(rows) {
  const headers = ['Date', 'Seal No.', 'WB No.', 'Reference No.', 'Van No.', 'Plate No.', 'Consignee', 'Amount (Billed)', 'Actual Amount', 'Minimum Applied', 'Remarks', 'Qty', 'Payment Status'];
  const csvRows = rows.map((row) => [
    formatDate(row.date),
    row.sealNo || '',
    row.wbNo || '',
    row.referenceNumber || '',
    row.vanNo || '',
    row.plateNo || '',
    row.consignee || '',
    row.amount || '',
    row.minimumApplied ? row.rawAmount || '' : '',   // actual amount column — blank if minimum not applied
    row.minimumApplied ? 'Yes' : '',
    row.remarks || '',
    row.qty || '',
    row.billingStatus === 'paid' ? 'Paid' : row.billingStatus === 'partially_paid' ? 'Partial' : row.billingStatus ? 'Unpaid' : 'No Billing',
  ]);
  const csv = [headers, ...csvRows]
    .map((record) => record.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'revenue-per-van-report.csv';
  link.click();
  URL.revokeObjectURL(url);
}

export default function RevenuePerVanPage() {
  const [dateFrom, setDateFrom] = useState(getStartOfMonth());
  const [dateTo, setDateTo] = useState(today());
  const [vanNo, setVanNo] = useState('');
  const [wbNo, setWbNo] = useState('');
  const [sealNo, setSealNo] = useState('');
  const [referenceNo, setReferenceNo] = useState('');
  const [data, setData] = useState(emptyData);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const companyName = useMemo(() => {
    return localStorage.getItem('accountingClientName') || 'ARVANNAH RENZ TRUCKING INC.';
  }, []);

  const loadReport = async () => {
    setLoading(true);
    try {
      const res = await waybillAPI.getRevenuePerVan({
        dateFrom,
        dateTo,
        ...(vanNo      ? { vanNo }      : {}),
        ...(wbNo       ? { wbNo }       : {}),
        ...(sealNo     ? { sealNo }     : {}),
        ...(referenceNo ? { referenceNo } : {}),
      });
      setData(res.data?.data || emptyData);
      setLoaded(true);
    } catch (e) {
      console.error(e);
      setData(emptyData);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, []);

  const vanKeys = useMemo(() => Object.keys(data.vanGroups || {}).sort(), [data.vanGroups]);
  const totalTrips = data.rows?.length || 0;
  const vanCount = vanKeys.length;
  const totalQty = useMemo(() => (data.rows || []).reduce((s, r) => s + (r.qty || 0), 0), [data.rows]);

  const handlePrint = () => {
    const params = new URLSearchParams({
      dateFrom,
      dateTo,
      ...(vanNo       ? { vanNo }       : {}),
      ...(wbNo        ? { wbNo }        : {}),
      ...(sealNo      ? { sealNo }      : {}),
      ...(referenceNo ? { referenceNo } : {}),
    });
    window.open(`/billing/reports/print/revenue-per-van?${params}`, '_blank');
  };

  const renderRow = (r) => (
    <tr key={`${r.waybillId}|${r.vanNo}|${r.sealNo}`} className="hover:bg-gray-50">
      <td>{formatDate(r.date)}</td>
      <td>{r.sealNo || ''}</td>
      <td>{r.wbNo}</td>
      <td className="text-center">{r.referenceNumber || ''}</td>
      <td>{r.vanNo}</td>
      <td>{r.consignee}</td>
      <td style={{ textAlign: 'right' }}>
        {r.amount ? `₱${PHP(r.amount)}` : ''}
        {r.minimumApplied && (
          <span title={`Minimum applied (actual: ₱${PHP(r.rawAmount)})`}
            className="ml-1 text-[10px] font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded px-1">
            min
          </span>
        )}
      </td>
      <td>{r.remarks || ''}</td>
      <td style={{ textAlign: 'right' }}>{r.qty || ''}</td>
      <td className="text-center"><PaymentStatusBadge status={r.billingStatus} /></td>
    </tr>
  );

  return (
    <Layout pageTitle="Revenue Per Van Report">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-header { display: block !important; }
          @page { size: A4 landscape; margin: 10mm; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #999; padding: 3px 6px; font-size: 9pt; }
          thead { background: #f3f4f6 !important; }
          .van-header { background: #e0f2f1 !important; }
          .van-total  { background: #fce4ec !important; }
        }
        .print-header { display: none; }
      `}</style>

      <div className="print-header mb-3">
        <p className="font-bold text-base">{companyName}</p>
        <p className="font-bold text-lg">REVENUE PER VAN REPORT</p>
        <p className="text-sm">Period: {dateFrom} to {dateTo}</p>
        <p className="text-sm">{vanNo ? `Van No.: ${vanNo}` : 'All Vans'}</p>
      </div>

      <div className="no-print bg-white border rounded-lg p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Date From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Date To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Van No.</label>
          <input type="text" value={vanNo} onChange={(e) => setVanNo(e.target.value)} className="border rounded px-2 py-1.5 text-sm w-44" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">WB No.</label>
          <input type="text" value={wbNo} onChange={(e) => setWbNo(e.target.value)} className="border rounded px-2 py-1.5 text-sm w-44" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Seal No.</label>
          <input type="text" value={sealNo} onChange={(e) => setSealNo(e.target.value)} className="border rounded px-2 py-1.5 text-sm w-36" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Reference No.</label>
          <input type="text" value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} placeholder="Search by reference no." className="border rounded px-2 py-1.5 text-sm w-44" />
        </div>
        <button onClick={loadReport} disabled={loading} className="bg-teal-600 text-white px-4 py-1.5 rounded text-sm disabled:opacity-50">
          {loading ? 'Loading...' : 'Load Report'}
        </button>
        {loaded && (data.rows || []).length > 0 && (
          <>
            <button onClick={handlePrint} className="bg-gray-600 text-white px-4 py-1.5 rounded text-sm">
              Print
            </button>
            <button onClick={() => exportCSV(data.rows || [])} className="bg-green-50 text-green-700 border border-green-200 px-4 py-1.5 rounded text-sm font-medium hover:bg-green-100">
              Export CSV
            </button>
          </>
        )}
      </div>

      <div className="no-print grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-white border rounded-lg p-3">
          <p className="text-xs text-gray-500">Total Trips</p>
          <p className="text-lg font-bold text-gray-800">{totalTrips}</p>
        </div>
        <div className="bg-white border rounded-lg p-3">
          <p className="text-xs text-gray-500">Grand Total</p>
          <p className="text-lg font-bold text-gray-800">₱{PHP(data.grandTotal || 0)}</p>
        </div>
        <div className="bg-white border rounded-lg p-3">
          <p className="text-xs text-gray-500">Vans</p>
          <p className="text-lg font-bold text-gray-800">{vanCount}</p>
        </div>
      </div>

      <div className="overflow-x-auto bg-white rounded-lg border">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-2 py-2 text-left">DATE</th>
              <th className="px-2 py-2 text-left">SEAL NO.</th>
              <th className="px-2 py-2 text-left">WB NO.</th>
              <th className="px-2 py-2 text-left">REFERENCE NO.</th>
              <th className="px-2 py-2 text-left">VAN NO.</th>
              <th className="px-2 py-2 text-left">CONSIGNEE</th>
              <th className="px-2 py-2 text-right">AMOUNT</th>
              <th className="px-2 py-2 text-left">REMARKS</th>
              <th className="px-2 py-2 text-right">QTY.</th>
              <th className="px-2 py-2 text-center">STATUS</th>
            </tr>
          </thead>
          <tbody>
            {(data.rows || []).length === 0 ? (
              <tr>
              <td colSpan={10} className="px-2 py-6 text-center text-gray-400">No records found.</td>
              </tr>
            ) : vanNo ? (
              (data.rows || []).map((r) => renderRow(r))
            ) : (
              vanKeys.map((key) => {
                const group = data.vanGroups[key];
                const first = group?.rows?.[0] || {};
                return (
                  <React.Fragment key={key}>
                    <tr className="van-header" style={{ background: '#e0f2f1' }}>
                      <td colSpan={10} className="px-2 py-2 font-semibold">
                        VAN NO.: {key}   Seal No.: {first.sealNo || '—'}   Plate No.: {first.plateNo || '—'}
                      </td>
                    </tr>
                    {(group.rows || []).map((r) => renderRow(r))}
                    <tr className="van-total" style={{ background: '#fce4ec' }}>
                      <td colSpan={6} style={{ textAlign: 'right', fontWeight: 700 }}>TOTAL</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>₱{PHP(group.totalAmount || 0)}</td>
                      <td />
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{group.totalQty || 0}</td>
                      <td />
                    </tr>
                  </React.Fragment>
                );
              })
            )}
          </tbody>
          {(data.rows || []).length > 0 && (
            <tfoot>
              <tr className="van-total" style={{ background: '#fce4ec' }}>
                <td colSpan={6} style={{ textAlign: 'right', fontWeight: 700 }}>TOTAL</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>₱{PHP(data.grandTotal || 0)}</td>
                <td />
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{totalQty}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Layout>
  );
}
